import {
  Divider,
  FormControl,
  FormLabel,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  AccountState,
  AzureOnboardingStatusStatusEnum,
  Provider,
} from '@repo/api-gw-sdk';
import { useState } from 'react';

import { TerraformBashToggle } from '@/components/accounts/terraformBashToggle';
import { CustomAccountDisplayName } from '@/components/form/customAccountDisplayName';
import { getCloudFieldConfig } from '@/components/form/validation/cloudFieldValidation';
import { Loader } from '@/components/layout/loading';
import { AzureTenantSelect } from '@/components/selectors/azureTenantSelect';
import { CopyableCode } from '@/components/shared/copyableCode';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';

import { StepErrorDialog } from '../../../../../components/dialogs/stepErrorDialog';
import { FormTextField } from '../../../../../components/form/formTextField';
import { BulletItem } from '../bulletItem';
import { DiscoveryConfigurationAccordion } from '../discoveryConfigurationAccordion';
import { extractErrorMessage } from '../errorHandling';
import { NumericBulletSection } from '../numericBulletSection';

export interface AzureSourceAccountState {
  tenantId: string;
  sourceConnectScope: 'managementGroup' | 'subscription';
  enableScopeSelection: boolean;
  sourceConnectMethod: 'terraform' | 'bash';
  sourceSubscriptionId: string;
  sourceManagementGroupId: string;
  eonInternalResourceGroupName?: string;
  onboardingFlow: boolean;
  name?: string;
}

const subscriptionIdConfig = getCloudFieldConfig(
  Provider.Azure,
  'subscriptionId'
);
const managementGroupIdConfig = getCloudFieldConfig(
  Provider.Azure,
  'managementGroupId'
);
const resourceGroupNameConfig = getCloudFieldConfig(
  Provider.Azure,
  'resourceGroupName'
);

export const ConnectAzureSourceAccount = <T extends AzureSourceAccountState>(
  props: StepProps<T>
) => {
  const [isDeploying, setIsDeploying] = useState(false);
  const [customName, setCustomName] = useState<string>('');
  const dal = useDAL();
  const { data: backupApps, isLoading } = dal.cloudAccounts.azureApps.useApps(
    'Source',
    props.wizardState.tenantId
  );
  const [error, setError] = useState<Error | null>(null);

  const sourceSubscriptionId =
    props.wizardState.sourceConnectScope === 'subscription'
      ? (props.wizardState.sourceSubscriptionId ?? '')
      : '';

  const sourceManagementGroupId =
    props.wizardState.sourceConnectScope === 'managementGroup'
      ? (props.wizardState.sourceManagementGroupId ?? '')
      : '';

  const terraformCode = `\
module "azure_source" {
  source = "https://eon.blob.core.windows.net/public/onboarding/latest/source/terraform/source.zip"
  management_app_id      = "${backupApps?.managementAppId ?? ''}"
  backup_app_id          = "${backupApps?.backupAppId ?? ''}"
  ${props.wizardState.sourceConnectScope === 'subscription' ? `subscription_id        = "${sourceSubscriptionId}"` : `management_group_id    = "${sourceManagementGroupId}"`}
}

output "source_onboarding" {
  value = module.azure_source
}`;

  const bashCode = `\
# Log in to the tenant
az login --tenant "${props.wizardState.tenantId}"
${
  sourceSubscriptionId
    ? `\n# Set the subscription ID
az account set --subscription "${sourceSubscriptionId}"\n`
    : ''
}
# Get access tokens from Azure
ARM_TOKEN=$(az account get-access-token --query accessToken -o tsv)
GRAPH_TOKEN=$(az account get-access-token --resource https://graph.microsoft.com/ --query accessToken -o tsv)

# Run the connection script
curl -fsSL https://eon.blob.core.windows.net/public/onboarding/latest/source/bash/source.sh -o source.sh && chmod +x source.sh && \\
AZURE_ACCESS_TOKEN="$ARM_TOKEN" \\
AZURE_GRAPH_TOKEN="$GRAPH_TOKEN" \\
./source.sh \\
  --subscription-id "${sourceSubscriptionId}" \\
  --management-group-id "${sourceManagementGroupId}" \\
  --management-app-id "${backupApps?.managementAppId ?? ''}" \\
  --backup-app-id "${backupApps?.backupAppId ?? ''}"
`;

  const code =
    props.wizardState.sourceConnectMethod === 'terraform'
      ? terraformCode
      : bashCode;

  const isMissingFields =
    !props.wizardState.tenantId ||
    (props.wizardState.sourceConnectScope === 'subscription'
      ? !props.wizardState.sourceSubscriptionId
      : !props.wizardState.sourceManagementGroupId);

  const canGoNext = () => {
    const validationErrors =
      props.wizardState.sourceConnectScope === 'subscription'
        ? !subscriptionIdConfig.validation.validateInput(
            props.wizardState.sourceSubscriptionId
          ) ||
          !props.wizardState.eonInternalResourceGroupName ||
          !resourceGroupNameConfig.validation.validateInput(
            props.wizardState.eonInternalResourceGroupName
          )
        : !managementGroupIdConfig.validation.validateInput(
            props.wizardState.sourceManagementGroupId
          );

    return !isDeploying && !validationErrors && !isMissingFields;
  };

  const connectManagementGroup = async () => {
    const response = await dal.cloudAccounts.managementGroups.connect({
      tenantId: props.wizardState.tenantId,
      sourceManagementGroupId: sourceManagementGroupId,
    });
    if (response.status !== AzureOnboardingStatusStatusEnum.Succeeded) {
      throw new Error('Failed to deploy management group');
    }
  };

  const connectSubscription = async () => {
    const { sourceAccount } = await dal.cloudAccounts.source.connect.azure({
      tenantId: props.wizardState.tenantId,
      sourceSubscriptionId: sourceSubscriptionId,
      sourceManagementGroupId: sourceManagementGroupId,
    });
    if (
      sourceAccount &&
      sourceAccount.status !== AccountState.ACCOUNT_STATE_CONNECTED
    ) {
      throw new Error('Failed to deploy source account');
    }
  };

  const onNextClick = async () => {
    setError(null);
    setIsDeploying(true);
    try {
      if (props.wizardState.sourceConnectScope === 'managementGroup') {
        await connectManagementGroup();
      } else {
        await connectSubscription();
      }
      await props.finish();
    } catch (error: unknown) {
      const errorMessage = await extractErrorMessage(error);
      setError(new Error(errorMessage));
    } finally {
      setIsDeploying(false);
    }
  };

  const bulletTitle = `Tell us ${props.wizardState.onboardingFlow ? 'what source' : props.wizardState.sourceConnectScope === 'subscription' ? 'which subscription' : 'which management group'} you want to connect, and then
                  ${
                    props.wizardState.sourceConnectMethod === 'terraform'
                      ? 'apply the generated Terraform module'
                      : 'run the generated Bash command'
                  } in your environment. ${props.wizardState.onboardingFlow ? ' If you connect a management group, all subscriptions under it will be connected.' : props.wizardState.sourceConnectScope === 'subscription' ? '' : ' All subscriptions under it will be connected.'}`;

  return (
    <>
      <StepContainer
        stepperIndex={3}
        hideStepper={!props.wizardState.onboardingFlow}
        stepperLabels={props.stepperLabels}
        canGoNext={canGoNext}
        nextButtonDisabledTooltip={
          isDeploying
            ? 'Please wait for the deployment to complete'
            : props.wizardState.sourceConnectScope === 'subscription'
              ? 'Please fill valid subscription ID'
              : 'Please fill valid management group ID'
        }
        nextButtonText={'Connect'}
        backButtonText={props.wizardState.onboardingFlow ? 'Back' : 'Cancel'}
        onBackClick={props.back}
        sx={{ overflow: 'auto', paddingX: '0px', paddingTop: '24px' }}
        onNextClick={onNextClick}
      >
        <Stack divider={<Divider />} gap='24px'>
          <TerraformBashToggle
            text={
              props.wizardState.onboardingFlow
                ? `Now we'll connect a source subscription or management group, and set the resources you want Eon to discover.
                Eon discovers all resources by default, but you can override this behavior with discovery configuration below.`
                : 'Before you start, make sure you can manage service principals in your Azure environment.'
            }
            connectMethod={props.wizardState.sourceConnectMethod}
            setConnectMethod={(connectMethod) => {
              props.setWizardState({
                ...props.wizardState,
                sourceConnectMethod: connectMethod,
              });
            }}
          />
          <Stack
            paddingX='min(100px,6vw)'
            paddingTop='16px'
            width='100%'
            height='auto'
          >
            {isLoading ? (
              <Loader />
            ) : (
              <Stack
                gap='40px'
                divider={<Divider sx={{ borderStyle: 'dashed' }} />}
              >
                <NumericBulletSection
                  bulletNumber={1}
                  title="Deploy Eon's source subscription"
                >
                  <BulletItem
                    title={bulletTitle}
                    leftContent={
                      <Stack
                        gap='24px'
                        divider={<Divider sx={{ borderStyle: 'dashed' }} />}
                      >
                        <Stack gap='24px'>
                          {!props.wizardState.onboardingFlow && (
                            <AzureTenantSelect
                              tenantId={props.wizardState.tenantId}
                              onChange={(tenantId) =>
                                props.setWizardState({
                                  ...props.wizardState,
                                  tenantId,
                                })
                              }
                            />
                          )}
                          {props.wizardState.enableScopeSelection && (
                            <FormControl>
                              <FormLabel>
                                <Typography variant='subtitle1'>
                                  Connection scope
                                </Typography>
                              </FormLabel>
                              <ToggleButtonGroup
                                sx={{ width: 'max-content' }}
                                exclusive
                                value={props.wizardState.sourceConnectScope}
                                onChange={(_, value) => {
                                  if (value === null) {
                                    return;
                                  }
                                  props.setWizardState({
                                    ...props.wizardState,
                                    sourceConnectScope: value,
                                  });
                                }}
                              >
                                <ToggleButton value='subscription'>
                                  Subscription
                                </ToggleButton>
                                <ToggleButton value='managementGroup'>
                                  Management group
                                </ToggleButton>
                              </ToggleButtonGroup>
                            </FormControl>
                          )}
                          <FormTextField
                            title='Subscription ID'
                            sx={{
                              display:
                                props.wizardState.sourceConnectScope ===
                                'subscription'
                                  ? 'inline-flex'
                                  : 'none',
                            }}
                            placeholder={subscriptionIdConfig.placeholder}
                            validation={subscriptionIdConfig.validation}
                            value={props.wizardState.sourceSubscriptionId || ''}
                            onChange={(event) =>
                              props.setWizardState({
                                ...props.wizardState,
                                sourceSubscriptionId: event.target.value,
                              })
                            }
                          />
                          <FormTextField
                            title='Management group ID'
                            sx={{
                              display:
                                props.wizardState.sourceConnectScope ===
                                'managementGroup'
                                  ? 'inline-flex'
                                  : 'none',
                            }}
                            placeholder={managementGroupIdConfig.placeholder}
                            validation={managementGroupIdConfig.validation}
                            value={
                              props.wizardState.sourceManagementGroupId || ''
                            }
                            onChange={(event) =>
                              props.setWizardState({
                                ...props.wizardState,
                                sourceManagementGroupId: event.target.value,
                              })
                            }
                          />
                        </Stack>
                        {props.wizardState.sourceConnectScope ===
                          'subscription' && (
                          <CustomAccountDisplayName
                            entityName='subscription'
                            customName={customName}
                            setCustomName={setCustomName}
                          />
                        )}
                      </Stack>
                    }
                    rightContent={
                      <CopyableCode
                        title={
                          props.wizardState.sourceConnectMethod === 'terraform'
                            ? 'main.tf'
                            : 'source.sh'
                        }
                        code={code}
                        blur={isMissingFields}
                      />
                    }
                  />
                </NumericBulletSection>
                {props.wizardState.sourceConnectScope === 'subscription' && (
                  <NumericBulletSection
                    bulletNumber={2}
                    title='Complete configuration'
                  >
                    <BulletItem
                      title={`After running the ${
                        props.wizardState.sourceConnectMethod === 'terraform'
                          ? 'Terraform module'
                          : 'Bash command'
                      }, paste the output value below:`}
                      leftContent={
                        <FormTextField
                          title='eon_internal_resource_group_name'
                          placeholder='eon-source-internal-rg'
                          validation={resourceGroupNameConfig.validation}
                          value={
                            props.wizardState.eonInternalResourceGroupName || ''
                          }
                          onChange={(event) =>
                            props.setWizardState({
                              ...props.wizardState,
                              eonInternalResourceGroupName: event.target.value,
                            })
                          }
                        />
                      }
                    />
                  </NumericBulletSection>
                )}
              </Stack>
            )}
            {props.wizardState.onboardingFlow && (
              <DiscoveryConfigurationAccordion />
            )}
            <div />
          </Stack>
        </Stack>
      </StepContainer>
      <StepErrorDialog
        isOpen={!!error}
        onClose={() => {
          setError(null);
          setIsDeploying(false);
        }}
        error={error}
      />
    </>
  );
};
